import { DvGridComponent } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvFormUtils,
  DvPanelModule,
} from '@devinco/ui/legacy';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AgGridModule } from 'ag-grid-angular';
import { TripImportGridService } from './trip-import-grid.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { TripDto } from '@pages/trips-page/components/trips-grid/interfaces';
import { Entity } from '@shared/constants/entity';
import {
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { FormOf } from '@devinco/ui/dds';
import { FormGroup } from '@angular/forms';
import { VehicleDto } from '@pages/vehicles-page/components/vehicles-grid/interfaces';
import { distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import DateUtils from '@shared/utils/date.utils';

@Component({
  selector: 'app-trip-import-grid',
  imports: [
    AgGridModule,
    DvGridComponent,
    DirectivesModule,
    DvPanelModule,
    DvDynamicFormModule,
  ],
  templateUrl: './trip-import-grid.component.html',
  providers: [GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      width: 100%;
      height: inherit;
    }
  `,
})
export class TripImportGridComponent {
  private gridOptionsService = inject(GridOptionsService);
  private gridService = inject(TripImportGridService);
  private destroyRef = inject(DestroyRef);

  tripSelectionChanged = output<TripDto[]>();

  gridOptions: GridOptions<TripDto>;
  panelFormElements: DvDynamicFormElement[];
  panelFormGroup: FormGroup<FormOf<Pick<VehicleDto, 'TrackerObjectID'>>>;

  private gridApi: GridApi<TripDto>;
  private defaultExternalFilters: FilterModelWithKeys;

  constructor() {
    this.setupPanelForm();
    this.setDefaultFilters();
    this.gridOptions = {
      ...this.gridOptionsService.serverSide(
        this.gridService.getColDefs(),
        Entity.TripWithPoints,
        undefined,
        [
          { colId: 'StartTime', sort: 'desc' },
          { colId: 'TrackerObjectID', sort: 'asc' },
        ]
      ),
      cacheBlockSize: 50,
      rowGroupPanelShow: 'never',
      rowSelection: {
        mode: 'multiRow',
        headerCheckbox: true,
      },
    };
  }

  onGridReady({ api }: GridReadyEvent<TripDto>): void {
    this.gridApi = api;
    api.setFilterModel(this.defaultExternalFilters);
  }

  onSelectionChanged(event: SelectionChangedEvent<TripDto>): void {
    // Ignore only OUR programmatic changes
    if (event.source === 'selectableChanged') return;

    const api = event.api;
    const selectedNodes = api.getSelectedNodes();

    if (selectedNodes.length < 2) {
      this.tripSelectionChanged.emit(selectedNodes.map(node => node.data!));
      return;
    }

    // deselect all rows if user selects one row from a different trackerObjectID (vehicleID)
    if (
      new Set(selectedNodes.map(node => node.data?.TrackerObjectID)).size > 1
    ) {
      event.api.deselectAll();
      return;
    }

    const indexes = selectedNodes.map(n => n.rowIndex!).sort((a, b) => a - b);
    const start = indexes[0];
    const end = indexes[indexes.length - 1];

    // Fill the gap
    for (let i = start; i <= end; i++) {
      api
        .getDisplayedRowAtIndex(i)
        ?.setSelected(true, false, 'selectableChanged');
    }

    this.tripSelectionChanged.emit(
      api.getSelectedNodes().map(node => node.data!)
    );
  }

  onFilterChanged(): void {
    /** clear selection when user filters something in the grid (due to it being SS) */
    this.gridApi.deselectAll();
  }

  private setDefaultFilters(): void {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 30);
    this.defaultExternalFilters = {
      StartTime: {
        filterType: 'date',
        operator: 'AND',
        conditions: [
          {
            dateFrom: `${DateUtils.convertDate(from)} 00:00:00`,
            dateTo: null,
            filterType: 'date',
            type: 'greaterThan',
          },
        ],
      },
    };
  }

  private setupPanelForm(): void {
    this.panelFormElements = this.gridService.getPanelFormElements();
    this.panelFormGroup = DvFormUtils.createFormGroup(this.panelFormElements);
    this.subscribeToPanelFormChanges();
  }

  private subscribeToPanelFormChanges(): void {
    this.panelFormGroup.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ TrackerObjectID }) => {
        this.gridApi.setFilterModel({
          ...this.gridApi.getFilterModel(),
          TrackerObjectID: {
            filterType: 'number',
            type: 'equals',
            filter: TrackerObjectID,
          },
        });
        this.gridApi.onFilterChanged();
      });
  }
}
